import React from "react";
import { Link } from "react-router-dom";
import {
  AlertItem,
  AlertListItem,
  AlertPreset,
  AlertType,
  DigestEntitlementBlockedReason,
  DigestFrequency,
  DIGEST_FREQUENCY_LABELS,
  DAY_OF_WEEK_LABELS,
} from "../../../types/grants";
import { StatusBadge } from "../../common/StatusBadge";

interface AlertCardProps {
  alert: AlertListItem;
  onEdit: (alert: AlertItem) => void;
  onDelete: (alertId: string) => void;
  onToggle: (alert: AlertItem) => void;
  isToggling: boolean;
  isDeleting: boolean;
}

/**
 * Display labels for presets (Polish)
 *
 * @see docs/specifications/2026-01-22-alerts-ux-simplification.md
 */
const PRESET_LABELS: Record<AlertPreset, string> = {
  [AlertPreset.NEW_CALLS_ONLY]: "Tylko nowe nabory",
  [AlertPreset.NEW_AND_CHANGES]: "Nowe nabory i zmiany",
  [AlertPreset.EVERYTHING]: "Wszystko",
};

/**
 * Fixed hour (Europe/Warsaw) at which UPCOMING_DIGEST emails are sent.
 * Mirrors the backend digest cron (`@Cron('0 8 * * *')`). The per-alert
 * `schedule.hours` field is NOT used by the digest path, so the card renders this
 * fixed time rather than the (legacy/irrelevant) stored schedule, which previously
 * showed a bogus second send hour (e.g. "8:00, 13:00").
 */
const DIGEST_SEND_HOUR = 8;

/**
 * Badge label, explanatory text, and CTA visibility for a blocked digest, keyed by
 * the reason from the backend capability gate. CHECK_FAILED is a transient billing
 * check failure, not an entitlement fact — it MUST use neutral copy and MUST NOT
 * show the payment CTA, in the VISIBLE text (not just a hover-only `title`), or a
 * paying customer sees a false "you need to pay" message that a screen-reader/mobile
 * user can't even discover is untrue (AC-5's "komunikat neutralny" requirement).
 *
 * @see docs/specifications/2026-07-13-digest-entitlement-soft-gate/spec.md AC-5, AC-6
 */
const DIGEST_BLOCK_CONFIG: Record<
  DigestEntitlementBlockedReason,
  { label: string; explanation: string; showsSubscriptionCta: boolean }
> = {
  [DigestEntitlementBlockedReason.NO_CAPABILITY]: {
    label: "Nieaktywny — wymaga aktywnej subskrypcji",
    explanation: "Ten digest nie zostanie wysłany, dopóki subskrypcja nie jest aktywna.",
    showsSubscriptionCta: true,
  },
  // OVER_CAPACITY means the tenant HAS an active subscription — the alert simply
  // doesn't fit within the per-user digest limit. Reusing NO_CAPABILITY's copy here
  // is a deliberate simplification (spec AC-6), not an oversight: today
  // DIGEST_MAX_PER_USER = 1 on every visible package, so OVER_CAPACITY is practically
  // unreachable. If a package with a limit > 1 is ever introduced, this needs its own
  // "you have more digests than your plan allows" copy instead of the subscription CTA.
  [DigestEntitlementBlockedReason.OVER_CAPACITY]: {
    label: "Nieaktywny — wymaga aktywnej subskrypcji",
    explanation: "Ten digest nie zostanie wysłany, dopóki subskrypcja nie jest aktywna.",
    showsSubscriptionCta: true,
  },
  [DigestEntitlementBlockedReason.CHECK_FAILED]: {
    label: "Nieaktywny — sprawdzamy dostęp",
    explanation:
      "Wystąpił przejściowy problem przy sprawdzaniu dostępu. Spróbujemy ponownie przy najbliższej wysyłce.",
    showsSubscriptionCta: false,
  },
};

/**
 * Format a digest schedule as a human-readable string.
 * e.g. "Co tydzień — Poniedziałek"
 */
function formatDigestSchedule(frequency: DigestFrequency | null | undefined, dayOfWeek: number | null | undefined): string {
  const freqLabel = frequency != null ? DIGEST_FREQUENCY_LABELS[frequency] ?? String(frequency) : "—";
  const dayLabel = dayOfWeek != null ? DAY_OF_WEEK_LABELS[dayOfWeek] ?? String(dayOfWeek) : "—";
  return `${freqLabel} — ${dayLabel}`;
}

/**
 * Card component displaying a single alert with actions.
 * Supports both EVENT_DRIVEN and UPCOMING_DIGEST alert types.
 *
 * @see docs/specifications/2026-05-26-upcoming-calls-digest-alert/spec.md
 */
const AlertCard: React.FC<AlertCardProps> = ({
  alert,
  onEdit,
  onDelete,
  onToggle,
  isToggling,
  isDeleting,
}) => {
  const alertType = alert.type ?? AlertType.EVENT_DRIVEN;
  const isDigest = alertType === AlertType.UPCOMING_DIGEST;

  // AC-6: capability gate only applies to enabled UPCOMING_DIGEST alerts — an alert
  // the user already turned off has nothing to explain, and EVENT_DRIVEN has its own
  // (unrelated) limit.
  const isDigestBlocked = isDigest && alert.isEnabled && alert.entitlementBlockedReason != null;
  const digestBlockReason = alert.entitlementBlockedReason ?? undefined;
  const digestBlockBadgeId = `digest-block-badge-${alert.id}`;
  const digestBlockExplanationId = `digest-block-explanation-${alert.id}`;

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("pl-PL", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatSchedule = () => {
    if (alert.schedule.mode === "IMMEDIATE") {
      return "Natychmiast (do 5 min)";
    }
    return `Zaplanowane: ${alert.schedule.hours.sort((a, b) => a - b).map((h) => `${h}:00`).join(", ")}`;
  };

  const hasFilters =
    (alert.filters.programs?.length ?? 0) > 0 ||
    (alert.filters.operators?.length ?? 0) > 0 ||
    (alert.filters.regions?.length ?? 0) > 0 ||
    alert.filters.minAllocation !== undefined ||
    alert.filters.maxAllocation !== undefined;

  return (
    <div
      className={`p-4 ${
        !alert.isEnabled ? "bg-neutral-50 opacity-75" : isDigestBlocked ? "bg-amber-50" : ""
      }`}
    >
      <div className="flex items-start justify-between gap-4">
        {/* Main content */}
        <div className="flex-1 min-w-0">
          {/* Name and status */}
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-lg font-medium text-neutral-900 truncate">
              {alert.name}
            </h3>
            {!alert.isEnabled && (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-neutral-200 text-neutral-600">
                Wyłączony
              </span>
            )}
            {isDigestBlocked && digestBlockReason && (
              <StatusBadge
                id={digestBlockBadgeId}
                status="pending"
                label={DIGEST_BLOCK_CONFIG[digestBlockReason].label}
                // Intentionally kept identical to `label`: it overrides the generic
                // default STATUS_ARIA_LABELS.pending ("Status: Oczekuje") with the
                // actual block reason, so a screen-reader user landing on this badge
                // directly (not via the toggle's aria-describedby) still hears the
                // specific message instead of a meaningless "pending" status.
                aria-label={DIGEST_BLOCK_CONFIG[digestBlockReason].label}
                title={DIGEST_BLOCK_CONFIG[digestBlockReason].explanation}
              />
            )}
          </div>
          {isDigestBlocked && digestBlockReason && (
            <p id={digestBlockExplanationId} className="mt-1 text-xs text-amber-700" role="note">
              {DIGEST_BLOCK_CONFIG[digestBlockReason].explanation}
              {DIGEST_BLOCK_CONFIG[digestBlockReason].showsSubscriptionCta && (
                <>
                  {" "}
                  <Link
                    to="/tenant/subscription"
                    className="underline font-medium hover:text-amber-800"
                  >
                    Przejdź do subskrypcji
                  </Link>
                </>
              )}
            </p>
          )}

          {/* Type badge + preset/schedule info */}
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            {isDigest ? (
              /* UPCOMING_DIGEST badge */
              <span
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800"
                title="Cykliczne podsumowanie nadchodzących naborów"
              >
                {/* Calendar icon */}
                <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                Nadchodzące nabory
              </span>
            ) : (
              /* EVENT_DRIVEN badge */
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                {PRESET_LABELS[alert.preset] || "Nowe nabory i zmiany"}
              </span>
            )}
          </div>

          {/* Details */}
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm text-neutral-600">
            {isDigest ? (
              <>
                <div>
                  <span className="font-medium">Częstotliwość:</span>{" "}
                  {formatDigestSchedule(alert.frequency, alert.dayOfWeek)}
                </div>
                <div>
                  <span className="font-medium">Harmonogram:</span>{" "}
                  {`o ${DIGEST_SEND_HOUR}:00 rano`}
                </div>
                <div>
                  <span className="font-medium">Ostatnia wysyłka:</span>{" "}
                  {formatDate(alert.lastMatchAt)}
                </div>
              </>
            ) : (
              <>
                <div>
                  <span className="font-medium">Harmonogram:</span>{" "}
                  {formatSchedule()}
                </div>
                <div>
                  <span className="font-medium">Dopasowania:</span>{" "}
                  {alert.matchCount}
                </div>
                <div>
                  <span className="font-medium">Ostatnie:</span>{" "}
                  {formatDate(alert.lastMatchAt)}
                </div>
              </>
            )}
          </div>

          {/* Filters summary */}
          {hasFilters && (
            <div className="mt-2 text-sm text-neutral-500">
              <span className="font-medium">Filtry:</span>{" "}
              {[
                alert.filters.programs?.length
                  ? `${alert.filters.programs.length} program(ów)`
                  : null,
                alert.filters.operators?.length
                  ? `${alert.filters.operators.length} operator(ów)`
                  : null,
                alert.filters.regions?.length
                  ? `${alert.filters.regions.length} region(ów)`
                  : null,
                alert.filters.minAllocation !== undefined ||
                alert.filters.maxAllocation !== undefined
                  ? "alokacja"
                  : null,
              ]
                .filter(Boolean)
                .join(", ")}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {/* Toggle switch */}
          <button
            onClick={() => onToggle(alert)}
            disabled={isToggling}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
              alert.isEnabled ? "bg-primary-600" : "bg-neutral-300"
            } ${isToggling ? "opacity-50" : ""}`}
            aria-label={alert.isEnabled ? "Wyłącz alert" : "Włącz alert"}
            aria-describedby={
              isDigestBlocked ? `${digestBlockBadgeId} ${digestBlockExplanationId}` : undefined
            }
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                alert.isEnabled ? "translate-x-6" : "translate-x-1"
              }`}
            />
          </button>

          {/* Edit button */}
          <button
            onClick={() => onEdit(alert)}
            className="p-2 text-neutral-500 hover:text-primary-600 hover:bg-neutral-100 rounded"
            title="Edytuj alert"
          >
            <svg
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
              />
            </svg>
          </button>

          {/* Delete button */}
          <button
            onClick={() => onDelete(alert.id)}
            disabled={isDeleting}
            className="p-2 text-neutral-500 hover:text-red-600 hover:bg-neutral-100 rounded disabled:opacity-50"
            title="Usuń alert"
          >
            <svg
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default AlertCard;
